import { mergeBackgroundWork, type BackgroundWorkPresentation } from '@/frontend/features/AgentChats/events/backgroundWork'
import type { AgentChatMessage } from '@/frontend/pages/Dashboard/AgentChats/types'

// Called only within the controller's existing session/org boundary. A native
// status is a projection of an immutable event, not a new message/event id.
// Therefore an older HTTP snapshot must not replace a newer Cable observation.
export function mergeNativeMessage(current: AgentChatMessage, next: AgentChatMessage): AgentChatMessage {
  if (current.id !== next.id) return next
  if (current.metadata.question_answer_status === 'rejected' && next.metadata.question_answer_status !== 'rejected') {
    next = { ...next, metadata: { ...next.metadata, question_answer_status: 'rejected' } }
  }
  if (current.role !== 'assistant' || next.role !== 'assistant' ||
    current.metadata.agent_chat_turn_id !== next.metadata.agent_chat_turn_id) return next
  const previousQuestion = current.metadata.user_question as { id?: string; state?: string } | undefined
  const nextQuestion = next.metadata.user_question as { id?: string; state?: string } | undefined
  if (previousQuestion?.id && previousQuestion.id === nextQuestion?.id &&
      previousQuestion.state !== 'pending' && nextQuestion.state === 'pending') {
    next = { ...next, metadata: { ...next.metadata, user_question: previousQuestion } }
  }
  const native = new Map<string, BackgroundWorkPresentation>()
  for (const event of current.activity_events || []) {
    const work = event.payload.backgroundWork
    if (work?.executionOnly && work.runId) native.set(`${event.turn_id}:${work.runId}`, work)
  }
  if (native.size === 0) return next
  const events = (next.activity_events || []).map((event) => {
    const work = event.payload.backgroundWork
    const previous = work?.runId && native.get(`${event.turn_id}:${work.runId}`)
    return previous ? { ...event, payload: { ...event.payload, backgroundWork: mergeBackgroundWork(previous, work) } } : event
  })
  // A GET started before the accepted tool result was ingested may omit that
  // immutable event altogether. Retain only already-authorized native evidence
  // for this same durable message/turn; never retain arbitrary legacy activity.
  const incomingIds = new Set(events.map((event) => event.id))
  const missing = (current.activity_events || []).filter((event) => event.id && !incomingIds.has(event.id) &&
    event.payload.backgroundWork?.executionOnly && event.payload.backgroundWork.runId)
  const activityEvents = missing.length === 0 ? events : [...events, ...missing].sort((a, b) =>
    (Date.parse(a.occurred_at || '') || 0) - (Date.parse(b.occurred_at || '') || 0))
  return {
    ...next,
    activity_events: activityEvents,
  }
}

export function upsertMessage(messages: AgentChatMessage[], nextMessage: AgentChatMessage) {
  const existing = messages.find((message) => message.id === nextMessage.id)
  if (!existing) return [...messages, nextMessage].sort(compareMessages)
  const merged = mergeNativeMessage(existing, nextMessage)
  return messages.map((message) => message.id === nextMessage.id ? merged : message).sort(compareMessages)
}

export function mergeMessages(current: AgentChatMessage[], next: AgentChatMessage[]) {
  return next.reduce<AgentChatMessage[]>((acc, message) => {
    const existing = acc.find((candidate) => candidate.id === message.id)
    if (existing && serializedMessageEqual(existing, message)) return acc
    return upsertMessage(acc, message)
  }, current)
}

// Preserve object identity on unchanged reconnect snapshots as before.
function serializedMessageEqual(left: AgentChatMessage, right: AgentChatMessage) {
  return left.role === right.role && left.content === right.content && left.user_id === right.user_id &&
    left.user_name === right.user_name && left.client_submission_id === right.client_submission_id &&
    left.created_at === right.created_at && JSON.stringify(left.metadata) === JSON.stringify(right.metadata) &&
    JSON.stringify(left.attachments) === JSON.stringify(right.attachments) &&
    JSON.stringify(left.activity_events ?? []) === JSON.stringify(right.activity_events ?? [])
}

function compareMessages(left: AgentChatMessage, right: AgentChatMessage) {
  return Date.parse(left.created_at) - Date.parse(right.created_at)
}
